import sortBy from "lodash.sortby";
import { Scale } from "lucide-react";
import React, { Dispatch, useMemo } from "react";

import { IgTextField, IgTypography } from "@intelligems/ig-theme";
import {
  StripeApiPriceDto,
  StripeApiSubscriptionItemPairDto,
} from "@intelligems/ig-types";
import { Grid, Skeleton, useTheme } from "@mui/material";
import { VirtualAutoComplete } from "@src/components/VirtualAutoComplete/VirtualAutoComplete";
import { filterByNameOrId } from "@src/features/admin/components/Fields/filter-by-name-or-id";
import {
  CreateOrModifySubscriptionReducerType,
  CreateSubscriptionReducerAction,
} from "@src/features/admin/pages/overview/components/Drawers/SubscriptionDrawer/subscriptionReducer";

const Formatter = (data: StripeApiPriceDto) => {
  const theme = useTheme();
  return (
    <Grid container columnSpacing={1} alignItems={"center"} flexWrap="nowrap">
      <Grid item>
        {data.meterId ? (
          <Scale size={12} style={{ marginTop: "8px" }} />
        ) : (
          <Scale
            color={theme.palette.text.placeholder}
            size={12}
            style={{ marginTop: "8px" }}
          />
        )}
      </Grid>
      <Grid item>
        <IgTypography variant={"body2"}>{data.name}</IgTypography>
      </Grid>
    </Grid>
  );
};

interface ChoosePriceFieldProps {
  showTemplates?: boolean;
  prices: StripeApiPriceDto[] | undefined;
  item: StripeApiSubscriptionItemPairDto;
  payload: CreateOrModifySubscriptionReducerType;
  dispatchPayload: Dispatch<CreateSubscriptionReducerAction>;
}

const getGroupByValue = (option: StripeApiPriceDto) =>
  option.name.includes("Overage")
    ? "2. Overages (Advanced - Use Template)"
    : "1. Prices";

export const ChoosePriceField = ({
  showTemplates,
  item,
  payload,
  dispatchPayload,
  prices,
}: ChoosePriceFieldProps) => {
  const sortedOptions = useMemo(() => {
    if (!prices) return [];
    return sortBy(prices, [
      (option) => getGroupByValue(option),
      (option) => option.name,
    ]);
  }, [prices]);

  if (!prices) {
    return <Skeleton variant="rectangular" height={40} />;
  }

  return (
    <VirtualAutoComplete
      sx={{
        "& .MuiInputBase-root": {
          height: "40px",
          fontSize: "12px",
        },
      }}
      componentsProps={{
        popper: { sx: { zIndex: 1800 } },
      }}
      formatter={Formatter}
      onChange={(e, newValue: StripeApiPriceDto) => {
        dispatchPayload({
          type: "addPriceToItemByIdx",
          value: newValue,
          priceId: item.price?.id,
        });
      }}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      options={sortedOptions}
      getOptionLabel={(option: StripeApiPriceDto) => option.name}
      renderInput={(params) => <IgTextField {...params} variant={"standard"} />}
      value={item.price}
      groupBy={getGroupByValue}
      disabled={item.isReadOnly}
      filterOptions={filterByNameOrId}
    />
  );
};
